import { FormEvent, ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  apiService,
  DASHBOARD_AUTH_CHANGED_EVENT,
  DashboardBooking,
  DashboardCalendarConnection,
  DashboardCalendarOption,
  DashboardEmailConnection,
  DashboardEmbedInfo,
  DashboardSite,
  DashboardSiteBilling,
  DashboardSiteInsights,
  getDashboardToken,
} from '@/api/apiService';
import {
  AssigneeRow,
  BreakRow,
  CrmProvider,
  DEFAULT_DAY_SCHEDULE,
  DateOverrideRow,
  DaySchedule,
  DayScheduleEntry,
  EDITABLE_ROLES,
  ServicePriceRow,
  ServiceRow,
  SiteConfig,
  UnavailableWindowRow,
  asLocalInputValue,
  prettyJson,
  sanitizeKey,
  todayLocalIso,
  toUtcIso,
} from './dashboardShared';

type DashboardSiteContextValue = ReturnType<typeof useDashboardSiteState>;

const DashboardSiteContext = createContext<DashboardSiteContextValue | null>(null);

function useDashboardSiteState() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  // Only one auth view is visible at a time: 'login' | 'forgot' | 'reset'
  const [authView, setAuthView] = useState<'login' | 'forgot' | 'reset'>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  // reset-password via link (token from URL)
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  // If a session token exists, wait for /me before showing the login form (avoids
  // login flash on remount / browser back into /dashboard).
  const [authReady, setAuthReady] = useState(() => !getDashboardToken());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [sites, setSites] = useState<DashboardSite[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [config, setConfig] = useState<SiteConfig | null>(null);

  // Structured form state
  // planTier: site subscription tier (starter | growth | pro). Controls how many/eager
  // revenue actions fire, and whether capability toggles below are editable.
  const [planTier, setPlanTier] = useState('starter');
  // capabilities.booking — offer scheduling / appointments in chat when intent is high enough
  const [bookingEnabled, setBookingEnabled] = useState(false);
  // capabilities.lead_capture — ask for contact details (name/email/…) without necessarily booking
  const [leadCaptureEnabled, setLeadCaptureEnabled] = useState(false);
  // capabilities.crm_sync — push qualified lead data toward CRM (via automation/n8n)
  const [crmSyncEnabled, setCrmSyncEnabled] = useState(false);
  // capabilities.human_handoff — escalate when visitor asks for a real person / agent
  const [humanHandoffEnabled, setHumanHandoffEnabled] = useState(false);
  // provider_options.human_handoff.contact_info — shown to the visitor when they ask for a person
  const [humanHandoffContactInfo, setHumanHandoffContactInfo] = useState('');
  // capabilities.widget_customization — whether this site may self-serve style the embed widget
  const [widgetCustomizationEnabled, setWidgetCustomizationEnabled] = useState(false);
  const [isWidgetCustomizerOpen, setIsWidgetCustomizerOpen] = useState(false);
  // booking_policy.can_book_in_chat — pick slots in-widget vs send visitor to an external scheduler URL
  const [canBookInChat, setCanBookInChat] = useState(false);
  // booking_policy.optional_lead_fields.* — extra questions on the in-chat lead/booking form
  const [leadFieldPhone, setLeadFieldPhone] = useState(false);
  const [leadFieldCompany, setLeadFieldCompany] = useState(false);
  const [leadFieldAddress, setLeadFieldAddress] = useState(false);
  const [leadFieldJobTitle, setLeadFieldJobTitle] = useState(false);
  const [leadFieldNotes, setLeadFieldNotes] = useState(false);
  const [leadFieldMarketingConsent, setLeadFieldMarketingConsent] = useState(false);
  const [hubspotCreateDeal, setHubspotCreateDeal] = useState(false);
  const [timezone, setTimezone] = useState('Europe/Sofia');
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [daySchedule, setDaySchedule] = useState<DaySchedule>(DEFAULT_DAY_SCHEDULE());
  const [dateOverrides, setDateOverrides] = useState<DateOverrideRow[]>([]);
  const [slotIntervalMinutes, setSlotIntervalMinutes] = useState('15');
  const [concurrentCapacity, setConcurrentCapacity] = useState('1');
  const [maxSuggestedSlots, setMaxSuggestedSlots] = useState('6');
  const [suggestionGap, setSuggestionGap] = useState('90');
  const [searchDays, setSearchDays] = useState('14');
  const [fallbackUrl, setFallbackUrl] = useState('');
  const [assignees, setAssignees] = useState<AssigneeRow[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [servicePricing, setServicePricing] = useState<ServicePriceRow[]>([]);
  const [priceCurrency, setPriceCurrency] = useState('EUR');
  const [breaks, setBreaks] = useState<BreakRow[]>([]);
  const [unavailableWindows, setUnavailableWindows] = useState<UnavailableWindowRow[]>([]);
  const [slackWebhook, setSlackWebhook] = useState('');
  const [teamEmail, setTeamEmail] = useState('');
  const [customerEmailFrom, setCustomerEmailFrom] = useState('');
  const [askOutcome, setAskOutcome] = useState(false);
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook] = useState('');
  // post_booking.human_handoff_slack_webhook_url — dedicated Slack channel for human_handoff only
  const [humanHandoffSlackWebhook, setHumanHandoffSlackWebhook] = useState('');
  const [saveToCrm, setSaveToCrm] = useState(true);
  const [automationEnabled, setAutomationEnabled] = useState(false);
  // Default HubSpot when sync is on — select options omit 'none', so 'none' looks selected as HubSpot
  // but hides the access-token field until the user toggles the provider.
  const [crmProvider, setCrmProvider] = useState<CrmProvider>('hubspot');
  const [crmWebhook, setCrmWebhook] = useState('');
  const [crmAuthToken, setCrmAuthToken] = useState('');
  const [hubspotAccessToken, setHubspotAccessToken] = useState('');

  // Admin-only raw editor
  const [advancedConfigJson, setAdvancedConfigJson] = useState('{}');

  const [insights, setInsights] = useState<DashboardSiteInsights | null>(null);
  const [billing, setBilling] = useState<DashboardSiteBilling | null>(null);
  const [bookings, setBookings] = useState<DashboardBooking[]>([]);
  const [bookingScope, setBookingScope] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [calendarConnection, setCalendarConnection] = useState<DashboardCalendarConnection | null>(null);
  const [calendarOptions, setCalendarOptions] = useState<DashboardCalendarOption[]>([]);
  const [calendarTargetId, setCalendarTargetId] = useState('');
  const [calendarSchedulingUrl, setCalendarSchedulingUrl] = useState('');
  const [emailConnection, setEmailConnection] = useState<DashboardEmailConnection | null>(null);
  const [isConnectingEmail, setIsConnectingEmail] = useState(false);
  const [embedInfo, setEmbedInfo] = useState<DashboardEmbedInfo | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [isUpdatingBookingId, setIsUpdatingBookingId] = useState<number | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);

  const selectedSiteRole = useMemo(
    () => sites.find((s) => s.id === selectedSiteId)?.role || '',
    [sites, selectedSiteId]
  );

  const canEdit = useMemo(
    () => selectedSiteId !== null && config !== null && EDITABLE_ROLES.includes(selectedSiteRole),
    [selectedSiteId, config, selectedSiteRole]
  );

  // Starter = mostly chat-only: booking / lead capture / CRM (and related lead fields,
  // schedule, catalog, CRM setup) are read-only + forced off. human_handoff stays editable.
  const isStarterPlan = planTier === 'starter';
  const canEditCapabilities = canEdit && !isStarterPlan;
  // Handoff is allowed on every plan (including Starter).
  const canEditHandoff = canEdit;

  const canCustomizeWidget = useMemo(
    () =>
      Boolean(embedInfo) &&
      (isPlatformAdmin || (embedInfo?.widget_customization_enabled && EDITABLE_ROLES.includes(selectedSiteRole))),
    [embedInfo, isPlatformAdmin, selectedSiteRole]
  );

  const enabledWeekDays = useMemo(
    () =>
      Object.entries(daySchedule)
        .filter(([, entry]) => entry.enabled)
        .map(([day]) => Number(day))
        .sort((a, b) => a - b),
    [daySchedule]
  );

  const visibleDateOverrides = useMemo(() => {
    const today = todayLocalIso();
    return dateOverrides
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !row.date || row.date >= today);
  }, [dateOverrides]);

  const assigneeOptions = useMemo(
    () =>
      assignees.map((a, idx) => ({
        key: a.id || sanitizeKey(a.name, `resource-${idx + 1}`),
        label: a.name || `Worker ${idx + 1}`,
      })),
    [assignees]
  );

  const syncFormFromConfig = (cfg: SiteConfig, calendarReady = false) => {
    const booking = (cfg.provider_options?.booking as Record<string, any>) || {};
    const crm = (cfg.provider_options?.crm_sync as Record<string, any>) || {};
    const postBooking = (cfg.post_booking as Record<string, any>) || {};
    const capabilities = (cfg.capabilities as Record<string, any>) || {};
    const bookingPolicy = (cfg.booking_policy as Record<string, any>) || {};
    const workingHours = (booking.working_hours as Record<string, any>) || {};

    setPlanTier(cfg.plan_tier || 'starter');
    setDefaultLanguage(String(cfg.default_language || 'en'));
    // Starter entitlement: booking / lead / CRM show as off even if stale config still has them on.
    // human_handoff is allowed on Starter (visitor can still ask for a real person).
    const starterLocked = (cfg.plan_tier || 'starter') === 'starter';
    setBookingEnabled(!starterLocked && Boolean(capabilities.booking?.enabled));
    setLeadCaptureEnabled(!starterLocked && Boolean(capabilities.lead_capture?.enabled));
    setCrmSyncEnabled(!starterLocked && Boolean(capabilities.crm_sync?.enabled));
    setHumanHandoffEnabled(Boolean(capabilities.human_handoff?.enabled));
    const humanHandoffOpts = (cfg.provider_options?.human_handoff as Record<string, any>) || {};
    setHumanHandoffContactInfo(String(humanHandoffOpts.contact_info || ''));
    setWidgetCustomizationEnabled(Boolean(capabilities.widget_customization?.enabled));
    // In-chat slots require a ready calendar; keep the toggle off until Schedule is connected.
    setCanBookInChat(!starterLocked && calendarReady && Boolean(bookingPolicy.can_book_in_chat));
    const optionalLeadFields = (bookingPolicy.optional_lead_fields as Record<string, boolean>) || {};
    setLeadFieldPhone(!starterLocked && Boolean(optionalLeadFields.phone));
    setLeadFieldCompany(!starterLocked && Boolean(optionalLeadFields.company));
    setLeadFieldAddress(!starterLocked && Boolean(optionalLeadFields.address));
    setLeadFieldJobTitle(!starterLocked && Boolean(optionalLeadFields.job_title));
    setLeadFieldNotes(!starterLocked && Boolean(optionalLeadFields.notes));
    setLeadFieldMarketingConsent(!starterLocked && Boolean(optionalLeadFields.marketing_consent));
    setHubspotCreateDeal(
      !starterLocked && (cfg.plan_tier || 'starter') === 'pro' && Boolean(bookingPolicy.hubspot_create_deal)
    );
    setTimezone(String(booking.timezone || bookingPolicy.timezone || 'Europe/Sofia'));
    const rawSchedule = workingHours.schedule as Record<string, any> | undefined;
    const nextSchedule = DEFAULT_DAY_SCHEDULE();
    if (rawSchedule && typeof rawSchedule === 'object') {
      for (let day = 0; day <= 6; day += 1) {
        const entry = rawSchedule[String(day)];
        if (entry) {
          nextSchedule[day] = {
            enabled: Boolean(entry.enabled),
            start: String(entry.start || '09:00'),
            end: String(entry.end || '17:00'),
          };
        }
      }
    } else {
      const legacyStart = String(workingHours.start || '09:00');
      const legacyEnd = String(workingHours.end || '17:00');
      const legacyDays: number[] = Array.isArray(workingHours.days)
        ? workingHours.days.map((d: any) => Number(d))
        : [0, 1, 2, 3, 4];
      for (let day = 0; day <= 6; day += 1) {
        nextSchedule[day] = { enabled: legacyDays.includes(day), start: legacyStart, end: legacyEnd };
      }
    }
    setDaySchedule(nextSchedule);
    setDateOverrides(
      Array.isArray(booking.date_overrides)
        ? booking.date_overrides
            .map((o: any) => ({
              date: String(o?.date || ''),
              enabled: o?.enabled !== false,
              start: String(o?.start || '09:00'),
              end: String(o?.end || '17:00'),
              reason: String(o?.reason || ''),
            }))
            .filter((o: DateOverrideRow) => o.date)
        : []
    );
    setSlotIntervalMinutes(String(booking.slot_interval_minutes ?? 15));
    setConcurrentCapacity(String(booking.concurrent_capacity ?? 1));
    setMaxSuggestedSlots(String(booking.max_suggested_slots ?? 6));
    setSuggestionGap(String(booking.suggestion_gap_minutes ?? 90));
    setSearchDays(String(booking.search_days ?? 14));
    setFallbackUrl(String(booking.fallback_scheduler_url || booking.booking_url || ''));
    setAssignees(
      Array.isArray(booking.assignees)
        ? booking.assignees.map((a: any, idx: number) => ({
            id: String(a?.id || a?.resource_key || sanitizeKey(String(a?.name || a || ''), `resource-${idx + 1}`)),
            name: String(a?.name || a?.label || a || ''),
          }))
        : []
    );
    setServices(
      Array.isArray(booking.services)
        ? booking.services.map((s: any) => ({
            name: String(s?.name || s?.label || s || ''),
            duration_minutes: s?.duration_minutes ? String(s.duration_minutes) : '',
            booking_url: String(s?.booking_url || s?.scheduler_url || ''),
          }))
        : []
    );
    setPriceCurrency(String(booking.price_currency || 'EUR').toUpperCase() || 'EUR');
    setServicePricing(
      Array.isArray(booking.service_pricing)
        ? booking.service_pricing.map((row: any) => ({
            service: String(row?.service || ''),
            assignee: String(row?.assignee || ''),
            amount: row?.amount != null ? String(row.amount) : '',
          }))
        : []
    );
    setBreaks(
      Array.isArray(booking.breaks)
        ? booking.breaks.map((b: any) => ({
            start: String(b?.start || '12:00'),
            end: String(b?.end || '13:00'),
            days: Array.isArray(b?.days)
              ? b.days.map((d: any) => Number(d)).filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)
              : [],
          }))
        : []
    );
    setUnavailableWindows(
      Array.isArray(booking.unavailable_windows)
        ? booking.unavailable_windows.map((u: any) => ({
            resource_key: String(u?.resource_key || ''),
            start_at_local: asLocalInputValue(String(u?.start_at || u?.start || '')),
            end_at_local: asLocalInputValue(String(u?.end_at || u?.end || '')),
            reason: String(u?.reason || u?.booked_for || ''),
          }))
        : []
    );
    setSlackWebhook(String(postBooking.slack_webhook_url || ''));
    setTeamEmail(String(postBooking.team_email || ''));
    setCustomerEmailFrom(String(postBooking.customer_email_from || ''));
    setAskOutcome(!starterLocked && Boolean(postBooking.ask_outcome));
    setGoogleReviewUrl(String(postBooking.google_review_url || ''));
    setNegativeFeedbackSlackWebhook(String(postBooking.negative_feedback_slack_webhook_url || ''));
    setHumanHandoffSlackWebhook(String(postBooking.human_handoff_slack_webhook_url || ''));
    const saveCrm = !starterLocked && postBooking.save_to_crm !== false;
    setSaveToCrm(saveCrm);
    setAutomationEnabled(Boolean((cfg.automation as Record<string, any>)?.enabled));
    const provider = String(crm.crm_provider || 'none').toLowerCase();
    const resolvedProvider: CrmProvider =
      provider === 'hubspot' || provider === 'webhook' ? provider : 'none';
    // Select only lists hubspot/webhook; keep state aligned so HubSpot token fields render.
    setCrmProvider(saveCrm && resolvedProvider === 'none' ? 'hubspot' : resolvedProvider);
    setCrmWebhook(String(crm.crm_webhook_url || ''));
    setCrmAuthToken(String(crm.crm_auth_token || ''));
    setHubspotAccessToken(
      String(crm.hubspot_access_token || crm.hubspot_private_app_token || '')
    );
    setAdvancedConfigJson(prettyJson(cfg));
  };

  const loadAuthState = async () => {
    try {
      const me = await apiService.dashboardMe();
      setIsAuthenticated(true);
      setIsPlatformAdmin(Boolean(me.user?.is_platform_admin));
      setMustResetPassword(Boolean(me.user?.must_reset_password));
      setUserEmail(String(me.user?.email || '').trim());
      const siteRows = await apiService.dashboardListSites();
      setSites(siteRows);
      const firstSite = siteRows[0]?.id;
      if (firstSite) {
        setSelectedSiteId(firstSite);
      }
      return { ok: true as const, sites: siteRows };
    } catch {
      setIsAuthenticated(false);
      setIsPlatformAdmin(false);
      setMustResetPassword(false);
      setUserEmail('');
      setSites([]);
      setSelectedSiteId(null);
      setConfig(null);
      return { ok: false as const, sites: [] as DashboardSite[] };
    } finally {
      setAuthReady(true);
    }
  };

  const loadSiteData = async (siteId: number) => {
    const [cfg, siteInsights, billingInfo, bookingRows, embed, calendarStatus, emailStatus] = await Promise.all([
      apiService.dashboardGetRevenueConfig(siteId),
      apiService.dashboardGetInsights(siteId),
      apiService.dashboardGetBilling(siteId).catch(() => null),
      apiService.dashboardListBookings(siteId, bookingScope),
      apiService.dashboardGetEmbedInfo(siteId),
      apiService.dashboardGetCalendarConnection(siteId).catch(() => null),
      apiService.dashboardGetEmailConnection(siteId).catch(() => null),
    ]);
    const connection = calendarStatus?.connection || null;
    const calendarReady = Boolean(connection?.connected) && !Boolean(connection?.needs_setup);
    setConfig(cfg.config);
    syncFormFromConfig(cfg.config, calendarReady);
    setInsights(siteInsights);
    setBilling(billingInfo);
    setBookings(bookingRows.bookings || []);
    setEmbedInfo(embed);
    setEmailConnection(emailStatus?.connection || null);
    setCalendarConnection(connection);
    setCalendarSchedulingUrl(connection?.scheduling_url || '');
    setCalendarTargetId(
      connection?.provider === 'google'
        ? connection.calendar_id || 'primary'
        : connection?.event_type_uri || ''
    );
    if (connection?.connected) {
      try {
        const optionsPayload = await apiService.dashboardListCalendarOptions(siteId);
        setCalendarOptions(optionsPayload.options || []);
      } catch {
        setCalendarOptions([]);
      }
    } else {
      setCalendarOptions([]);
    }
  };

  // Auto-detect reset_token in URL (from email link) and switch to the reset view
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('reset_token');
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setAuthView('reset');
      // Clean the token out of the URL without a page reload
      const clean = new URL(window.location.href);
      clean.searchParams.delete('reset_token');
      window.history.replaceState({}, '', clean.toString());
    }
  }, []);

  useEffect(() => {
    loadAuthState();
  }, []);

  useEffect(() => {
    const onAuthChanged = () => {
      if (!getDashboardToken()) {
        setIsAuthenticated(false);
        setUserEmail('');
        setSites([]);
        setSelectedSiteId(null);
        setConfig(null);
        setAuthReady(true);
        return;
      }
      setAuthReady(false);
      void loadAuthState();
    };
    window.addEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    return () => {
      window.removeEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!selectedSiteId || !isAuthenticated) return;
    loadSiteData(selectedSiteId).catch((error) => {
      console.error(error);
      toast.error('Failed to load dashboard site data');
    });
  }, [selectedSiteId, isAuthenticated, bookingScope]);

  useEffect(() => {
    if (!isAuthenticated || selectedSiteId || isPlatformAdmin) return;
    apiService
      .dashboardGetAccountBilling()
      .then(setBilling)
      .catch(() => setBilling(null));
  }, [isAuthenticated, selectedSiteId, isPlatformAdmin]);

  // OAuth connect uses a full-page redirect. Browser Back / closing the consent page often
  // restores this document from bfcache with isConnecting* still true (button stuck disabled).
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setIsConnectingCalendar(false);
      setIsConnectingEmail(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarConnected = params.get('calendar_connected');
    const calendarError = params.get('calendar_error');
    if (!calendarConnected && !calendarError) return;
    setIsConnectingCalendar(false);
    if (calendarConnected) {
      toast.success(`Calendar connected (${calendarConnected})`);
    }
    if (calendarError) {
      toast.error(`Calendar connection failed: ${calendarError}`);
    }
    const clean = new URL(window.location.href);
    clean.searchParams.delete('calendar_connected');
    clean.searchParams.delete('calendar_error');
    window.history.replaceState({}, '', clean.toString());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailConnected = params.get('email_connected');
    const emailError = params.get('email_error');
    if (!emailConnected && !emailError) return;
    setIsConnectingEmail(false);
    if (emailConnected) {
      toast.success('Gmail connected for visitor booking emails');
      if (selectedSiteId) {
        void loadSiteData(selectedSiteId);
      }
    }
    if (emailError) {
      toast.error(`Gmail connection failed: ${emailError}`);
    }
    const clean = new URL(window.location.href);
    clean.searchParams.delete('email_connected');
    clean.searchParams.delete('email_error');
    window.history.replaceState({}, '', clean.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot URL toast after OAuth return
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const billingStatus = params.get('billing');
    if (!billingStatus) return;

    const siteFromQuery = Number(params.get('site_id') || 0);
    const scope = params.get('scope') || '';
    const sessionId = params.get('session_id') || '';
    const clean = new URL(window.location.href);
    clean.searchParams.delete('billing');
    clean.searchParams.delete('site_id');
    clean.searchParams.delete('scope');
    clean.searchParams.delete('session_id');
    window.history.replaceState({}, '', clean.toString());

    if (billingStatus === 'cancel' || billingStatus === 'pm_cancel') {
      toast('Checkout cancelled');
      return;
    }

    const isAccountScope = scope === 'account' || (!siteFromQuery && !selectedSiteId);

    if (isAccountScope) {
      if ((billingStatus === 'success' || billingStatus === 'pm_saved') && sessionId) {
        apiService
          .dashboardConfirmAccountBillingCheckout(sessionId)
          .then((result) => {
            if (result.billing) setBilling(result.billing);
            if (billingStatus === 'pm_saved') {
              toast.success('Payment method saved');
            } else {
              const testNote = result.stripe_test_mode ? ' (test mode)' : '';
              toast.success(`Balance updated${testNote}`);
            }
          })
          .catch(() => {
            toast.error(
              billingStatus === 'pm_saved'
                ? 'Returned from Stripe, but payment method confirm failed — refresh or check webhook'
                : 'Payment returned, but balance confirm failed — refresh or check webhook'
            );
            apiService.dashboardGetAccountBilling().then(setBilling).catch(() => null);
          });
        return;
      }
      if (billingStatus === 'pm_saved' || billingStatus === 'success') {
        toast.success(billingStatus === 'pm_saved' ? 'Payment method saved' : 'Payment completed');
        apiService.dashboardGetAccountBilling().then(setBilling).catch(() => null);
      }
      return;
    }

    const targetSiteId = siteFromQuery || selectedSiteId;
    if (!targetSiteId) return;

    if (siteFromQuery && siteFromQuery !== selectedSiteId) {
      setSelectedSiteId(siteFromQuery);
    }

    if ((billingStatus === 'success' || billingStatus === 'pm_saved') && sessionId) {
      apiService
        .dashboardConfirmBillingCheckout(targetSiteId, sessionId)
        .then((result) => {
          if (result.billing) setBilling(result.billing);
          if (billingStatus === 'pm_saved') {
            toast.success('Payment method saved');
          } else {
            const testNote = result.stripe_test_mode ? ' (test mode)' : '';
            toast.success(`Balance updated${testNote}`);
          }
        })
        .catch(() => {
          toast.error(
            billingStatus === 'pm_saved'
              ? 'Returned from Stripe, but payment method confirm failed — refresh or check webhook'
              : 'Payment returned, but balance confirm failed — refresh or check webhook'
          );
          apiService.dashboardGetBilling(targetSiteId).then(setBilling).catch(() => null);
        });
      return;
    }

    if (billingStatus === 'pm_saved' || billingStatus === 'success') {
      toast.success(billingStatus === 'pm_saved' ? 'Payment method saved' : 'Payment completed');
      apiService.dashboardGetBilling(targetSiteId).then(setBilling).catch(() => null);
    }
  }, [isAuthenticated, selectedSiteId]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      await apiService.dashboardLogin(email.trim(), password);
      const result = await loadAuthState();
      toast.success('Signed in');
      // Replace the login history entry so browser Back does not reopen the form.
      navigate(result.sites.length === 0 ? '/dashboard/content' : '/dashboard', { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401 || detail === 'Invalid credentials') {
        setLoginError('Invalid email or password.');
      } else {
        setLoginError(typeof detail === 'string' ? detail : 'Sign in failed. Please try again.');
      }
    }
  };

  const handleAcceptInvite = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await apiService.dashboardAcceptInvite(inviteToken.trim(), invitePassword, inviteName.trim() || undefined);
      const result = await loadAuthState();
      toast.success('Invite accepted');
      navigate(result.sites.length === 0 ? '/dashboard/content' : '/dashboard', { replace: true });
    } catch {
      toast.error('Invite acceptance failed');
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setIsForgotLoading(true);
    try {
      await apiService.dashboardForgotPassword(forgotEmail.trim());
      setForgotSubmitted(true);
    } catch {
      // Still show the same success-like message to avoid email enumeration,
      // but surface a generic error for connectivity issues
      toast.error('Something went wrong. Please try again in a moment.');
    } finally {
      setIsForgotLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetNewPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsResettingPassword(true);
    try {
      await apiService.dashboardResetPassword(resetToken.trim(), resetNewPassword);
      toast.success('Password updated — you can now sign in');
      setResetToken('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setAuthView('login');
    } catch {
      toast.error('Something went wrong. The link may have expired or already been used.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const updateDaySchedule = (day: number, field: keyof DayScheduleEntry, value: string | boolean) => {
    setDaySchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const addDateOverride = () => {
    setDateOverrides((prev) => [
      ...prev,
      { date: todayLocalIso(), enabled: false, start: '09:00', end: '17:00', reason: '' },
    ]);
  };

  const updateDateOverride = (index: number, field: keyof DateOverrideRow, value: string | boolean) => {
    setDateOverrides((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const removeDateOverride = (index: number) => {
    setDateOverrides((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAssignee = (index: number, name: string) => {
    setAssignees((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const fallback = `resource-${index + 1}`;
        return { ...row, name, id: sanitizeKey(name, row.id || fallback) };
      })
    );
  };

  const updateService = (index: number, field: keyof ServiceRow, value: string) => {
    setServices((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const updateServicePrice = (index: number, field: keyof ServicePriceRow, value: string) => {
    setServicePricing((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const serviceNameOptions = useMemo(
    () => services.map((s) => s.name.trim()).filter(Boolean),
    [services]
  );
  const assigneeNameOptions = useMemo(
    () => assignees.map((a) => a.name.trim()).filter(Boolean),
    [assignees]
  );

  const updateBreak = (index: number, field: keyof BreakRow, value: string | number[]) => {
    setBreaks((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const toggleBreakDay = (index: number, day: number) => {
    setBreaks((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const nextDays = row.days.includes(day)
          ? row.days.filter((d) => d !== day)
          : [...row.days, day].sort((a, b) => a - b);
        return { ...row, days: nextDays };
      })
    );
  };

  const updateUnavailableWindow = (index: number, field: keyof UnavailableWindowRow, value: string) => {
    setUnavailableWindows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSaveConfig = async () => {
    if (!selectedSiteId || !config) return;
    try {
      setIsSaving(true);

      const calendarReady =
        Boolean(calendarConnection?.connected) && !Boolean(calendarConnection?.needs_setup);
      if (!isStarterPlan && bookingEnabled && canBookInChat && !calendarReady) {
        toast.error(
          'Allow booking inside chat needs a connected calendar with a target chosen (Schedule). Connect Google or Calendly first, or turn off in-chat booking and use a fallback scheduler link.'
        );
        setIsSaving(false);
        return;
      }
      if (!isStarterPlan && bookingEnabled && !canBookInChat && !String(fallbackUrl || '').trim()) {
        toast.error(
          'To enable Booking, turn on Allow booking inside chat (after connecting a calendar) or add a fallback scheduler link on Schedule.'
        );
        setIsSaving(false);
        return;
      }

      let advancedOverride: Partial<SiteConfig> = {};
      if (isPlatformAdmin && advancedConfigJson.trim()) {
        try {
          advancedOverride = JSON.parse(advancedConfigJson);
        } catch {
          toast.error('Advanced JSON is invalid');
          setIsSaving(false);
          return;
        }
      }

      const merged: SiteConfig = {
        ...config,
        ...advancedOverride,
        plan_tier: planTier,
        default_language: defaultLanguage,
        capabilities: {
          ...(config.capabilities || {}),
          ...((advancedOverride as SiteConfig).capabilities || {}),
          // Each capability.enabled flag is what choose_capabilities() checks before firing.
          booking: {
            ...((config.capabilities as Record<string, any>)?.booking || {}),
            // Force off on Starter so chat stays Q&A-only.
            enabled: isStarterPlan ? false : bookingEnabled,
          },
          lead_capture: {
            ...((config.capabilities as Record<string, any>)?.lead_capture || {}),
            enabled: isStarterPlan ? false : leadCaptureEnabled,
          },
          crm_sync: {
            ...((config.capabilities as Record<string, any>)?.crm_sync || {}),
            enabled: isStarterPlan ? false : crmSyncEnabled,
          },
          human_handoff: {
            ...((config.capabilities as Record<string, any>)?.human_handoff || {}),
            // Allowed on every plan (including Starter). Use n8n when on so automation can notify the team.
            enabled: humanHandoffEnabled,
            provider: humanHandoffEnabled
              ? 'n8n'
              : ((config.capabilities as Record<string, any>)?.human_handoff?.provider || 'noop'),
          },
          widget_customization: {
            ...((config.capabilities as Record<string, any>)?.widget_customization || {}),
            enabled: widgetCustomizationEnabled,
          },
        },
        booking_policy: {
          ...(config.booking_policy || {}),
          ...((advancedOverride as SiteConfig).booking_policy || {}),
          can_book_in_chat: isStarterPlan ? false : canBookInChat,
          optional_lead_fields: {
            phone: isStarterPlan ? false : leadFieldPhone,
            company: isStarterPlan ? false : leadFieldCompany,
            address: isStarterPlan ? false : leadFieldAddress,
            job_title: isStarterPlan ? false : leadFieldJobTitle,
            notes: isStarterPlan ? false : leadFieldNotes,
            marketing_consent: isStarterPlan ? false : leadFieldMarketingConsent,
          },
          hubspot_create_deal: planTier === 'pro' ? hubspotCreateDeal : false,
        },
        post_booking: {
          ...(config.post_booking || {}),
          ...((advancedOverride as SiteConfig).post_booking || {}),
          slack_webhook_url: slackWebhook || '',
          team_email: teamEmail || '',
          customer_email_from: customerEmailFrom || '',
          save_to_crm: isStarterPlan ? false : saveToCrm,
          ask_outcome: isStarterPlan ? false : askOutcome,
          google_review_url: googleReviewUrl || '',
          negative_feedback_slack_webhook_url: negativeFeedbackSlackWebhook || '',
          human_handoff_slack_webhook_url: humanHandoffSlackWebhook || '',
        },
        automation: {
          ...(config.automation || {}),
          ...((advancedOverride as SiteConfig).automation || {}),
          enabled: automationEnabled,
          provider: 'n8n',
        },
        provider_options: {
          ...(config.provider_options || {}),
          ...((advancedOverride as SiteConfig).provider_options || {}),
          crm_sync: {
            ...((config.provider_options as Record<string, any>)?.crm_sync || {}),
            crm_provider:
              isStarterPlan || !saveToCrm
                ? 'none'
                : crmProvider === 'webhook'
                  ? 'webhook'
                  : 'hubspot',
            crm_webhook_url:
              !isStarterPlan && saveToCrm && crmProvider === 'webhook' ? crmWebhook || '' : '',
            crm_auth_token:
              !isStarterPlan && saveToCrm && crmProvider === 'webhook' ? crmAuthToken || '' : '',
            hubspot_access_token:
              !isStarterPlan && saveToCrm && crmProvider !== 'webhook' ? hubspotAccessToken || '' : '',
          },
          human_handoff: {
            ...((config.provider_options as Record<string, any>)?.human_handoff || {}),
            // Shown to the visitor when they ask for a person (see Plan & Booking Behaviour card).
            contact_info: humanHandoffContactInfo || '',
          },
          booking: {
            ...((config.provider_options as Record<string, any>)?.booking || {}),
            timezone,
            slot_interval_minutes: Number(slotIntervalMinutes) || 15,
            concurrent_capacity: Number(concurrentCapacity) || 1,
            max_suggested_slots: Number(maxSuggestedSlots) || 6,
            suggestion_gap_minutes: Number(suggestionGap) || 90,
            search_days: Number(searchDays) || 14,
            fallback_scheduler_url: fallbackUrl || '',
            working_hours: {
              schedule: Object.fromEntries(
                Object.entries(daySchedule).map(([day, entry]) => [
                  day,
                  { enabled: entry.enabled, start: entry.start, end: entry.end },
                ])
              ),
            },
            date_overrides: dateOverrides
              .filter((o) => o.date && o.date >= todayLocalIso())
              .map((o) => ({
                date: o.date,
                enabled: o.enabled,
                ...(o.enabled ? { start: o.start, end: o.end } : {}),
                ...(o.reason.trim() ? { reason: o.reason.trim() } : {}),
              })),
            assignees: assignees
              .filter((a) => a.name.trim())
              .map((a, idx) => {
                const cleanName = a.name.trim();
                const key = a.id || sanitizeKey(cleanName, `resource-${idx + 1}`);
                return { id: key, resource_key: key, name: cleanName };
              }),
            services: services
              .filter((s) => s.name.trim())
              .map((s) => ({
                name: s.name.trim(),
                duration_minutes: Number(s.duration_minutes) || 0,
                booking_url: s.booking_url.trim(),
              })),
            price_currency: priceCurrency || 'EUR',
            service_pricing: servicePricing
              .filter((row) => row.service.trim() && row.amount.trim())
              .map((row) => ({
                service: row.service.trim(),
                assignee: row.assignee.trim(),
                amount: Number(row.amount),
                currency: priceCurrency || 'EUR',
              })),
            breaks: breaks
              .filter((b) => b.start.trim() && b.end.trim())
              .map((b) => ({ start: b.start.trim(), end: b.end.trim(), days: b.days })),
            unavailable_windows: unavailableWindows
              .filter((u) => u.start_at_local.trim() && u.end_at_local.trim())
              .map((u) => ({
                resource_key: u.resource_key.trim(),
                start_at: toUtcIso(u.start_at_local.trim()),
                end_at: toUtcIso(u.end_at_local.trim()),
                reason: u.reason.trim(),
              }))
              .filter((u) => u.start_at && u.end_at),
          },
        },
      };

      const saved = await apiService.dashboardSaveRevenueConfig(selectedSiteId, merged);
      setConfig(saved.config);
      const calendarReadyAfterSave =
        Boolean(calendarConnection?.connected) && !Boolean(calendarConnection?.needs_setup);
      syncFormFromConfig(saved.config, calendarReadyAfterSave);
      toast.success('Configuration saved');
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    await apiService.dashboardLogout();
    setIsAuthenticated(false);
    setIsPlatformAdmin(false);
    setUserEmail('');
    setSites([]);
    setSelectedSiteId(null);
    setConfig(null);
    setBookings([]);
    setInsights(null);
    setEmbedInfo(null);
    toast.success('Signed out');
    navigate('/dashboard', { replace: true });
  };

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const formatBookingWhen = (booking: DashboardBooking) => {
    if (booking.slot_label) return booking.slot_label;
    const start = new Date(booking.start_at);
    if (Number.isNaN(start.getTime())) return booking.start_at;
    return start.toLocaleString();
  };

  const formatBookingCustomerExtras = (booking: DashboardBooking) => {
    const parts: string[] = [];
    if (booking.lead_company) parts.push(booking.lead_company);
    if (booking.lead_phone) parts.push(booking.lead_phone);
    if (booking.lead_job_title) parts.push(booking.lead_job_title);
    if (booking.lead_address) parts.push(booking.lead_address);
    return parts;
  };

  const formatBookingAttribution = (booking: DashboardBooking) => {
    const parts: string[] = [];
    if (booking.page_url) parts.push(booking.page_url);
    const utmBits = [booking.utm_source, booking.utm_medium, booking.utm_campaign].filter(Boolean);
    if (utmBits.length > 0) parts.push(`UTM: ${utmBits.join(' / ')}`);
    return parts;
  };

  const handleBookingStatusUpdate = async (
    bookingId: number,
    status: 'cancelled' | 'no_show'
  ) => {
    if (!selectedSiteId || !canEdit) return;
    const confirmMessage =
      status === 'cancelled'
        ? 'Cancel this booking? It will be removed from availability and deleted from the connected calendar if synced.'
        : 'Mark this booking as no-show? The calendar event will be kept for your records.';
    if (!window.confirm(confirmMessage)) return;
    try {
      setIsUpdatingBookingId(bookingId);
      const result = await apiService.dashboardUpdateBookingStatus(selectedSiteId, bookingId, status);
      const refreshed = await apiService.dashboardListBookings(selectedSiteId, bookingScope);
      setBookings(refreshed.bookings || []);
      if (result.calendar_warning) {
        toast(
          status === 'cancelled'
            ? `Booking cancelled, but calendar update failed: ${result.calendar_warning}`
            : `Updated in dashboard, but calendar warning: ${result.calendar_warning}`
        );
      } else {
        toast.success(status === 'cancelled' ? 'Booking cancelled' : 'Marked as no-show');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update booking');
    } finally {
      setIsUpdatingBookingId(null);
    }
  };

  const handleRetryCalendarSync = async (bookingId: number) => {
    if (!selectedSiteId || !canEdit) return;
    try {
      setIsUpdatingBookingId(bookingId);
      const result = await apiService.dashboardRetryBookingCalendarSync(selectedSiteId, bookingId);
      const refreshed = await apiService.dashboardListBookings(selectedSiteId, bookingScope);
      setBookings(refreshed.bookings || []);
      if (result.ok) {
        toast.success('Calendar sync completed');
      } else {
        toast.error(result.calendar_warning || 'Calendar sync failed');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to retry calendar sync');
    } finally {
      setIsUpdatingBookingId(null);
    }
  };

  const handleConnectCalendar = async (provider: 'google' | 'calendly') => {
    if (!selectedSiteId || isConnectingCalendar) return;
    try {
      setIsConnectingCalendar(true);
      const redirectUrl = await apiService.dashboardStartCalendarConnect(selectedSiteId, provider);
      window.location.assign(redirectUrl);
      // If navigation is cancelled (Back / close consent), clear so the CTA is usable again.
      window.setTimeout(() => setIsConnectingCalendar(false), 1500);
    } catch (error: any) {
      setIsConnectingCalendar(false);
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to start calendar connect');
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!selectedSiteId || !canEdit) return;
    if (
      !window.confirm(
        'Disconnect calendar?\n\n' +
          'In-chat booking will be turned off — visitors cannot pick time slots in the widget without a connected calendar.\n\n' +
          'If you have a fallback scheduler link, Booking can stay on (external link only). Otherwise Booking will also be disabled.\n\n' +
          'This does not disconnect Gmail. Booking confirmation emails keep using your Gmail connection if it is still linked.'
      )
    ) {
      return;
    }
    try {
      const result = await apiService.dashboardDisconnectCalendar(selectedSiteId);
      const changes = result.booking_changes;
      if (changes?.booking_disabled_for_missing_fallback) {
        toast.success('Calendar disconnected. In-chat booking and Booking were turned off (no fallback scheduler link).');
      } else if (changes?.cleared_in_chat_booking) {
        toast.success('Calendar disconnected. In-chat booking turned off; Booking stays on via your fallback link.');
      } else {
        toast.success('Calendar disconnected');
      }
      await loadSiteData(selectedSiteId);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to disconnect calendar');
    }
  };

  const handleConnectEmail = async () => {
    if (!selectedSiteId || isConnectingEmail) return;
    try {
      setIsConnectingEmail(true);
      const redirectUrl = await apiService.dashboardStartEmailConnect(selectedSiteId);
      window.location.assign(redirectUrl);
      // If navigation is cancelled (Back / close consent), clear so the CTA is usable again.
      window.setTimeout(() => setIsConnectingEmail(false), 1500);
    } catch (error: any) {
      setIsConnectingEmail(false);
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to start Gmail connect');
    }
  };

  const handleDisconnectEmail = async () => {
    if (!selectedSiteId || !canEdit) return;
    if (
      !window.confirm(
        'Disconnect Gmail for visitor booking emails?\n\n' +
          'Confirmations will fall back to EmbedReply delivery until you reconnect.\n\n' +
          'This does not turn off Booking or disconnect your calendar. In-chat slots still require a connected calendar.'
      )
    ) {
      return;
    }
    try {
      await apiService.dashboardDisconnectEmail(selectedSiteId);
      toast.success('Gmail disconnected — booking stays on; emails use EmbedReply until you reconnect');
      await loadSiteData(selectedSiteId);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to disconnect Gmail');
    }
  };

  const handleSaveCalendarSettings = async () => {
    if (!selectedSiteId || !canEdit || !calendarConnection?.connected) return;
    try {
      setIsSaving(true);
      const payload =
        calendarConnection.provider === 'google'
          ? { calendar_id: calendarTargetId, scheduling_url: calendarSchedulingUrl || undefined }
          : { event_type_uri: calendarTargetId, scheduling_url: calendarSchedulingUrl || undefined };
      const result = await apiService.dashboardUpdateCalendarSettings(selectedSiteId, payload);
      setCalendarConnection(result.connection);
      setCalendarSchedulingUrl(result.connection.scheduling_url || calendarSchedulingUrl);
      if (result.connection.scheduling_url) {
        setFallbackUrl(result.connection.scheduling_url);
      }
      toast.success('Calendar settings saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save calendar settings');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCalendarSyncBadge = (booking: DashboardBooking) => {
    const status = String(booking.calendar_sync_status || '').trim();
    if (!status || status === 'skipped') return 'No calendar';
    if (status === 'synced') return 'Synced';
    if (status === 'failed') return 'Sync failed';
    return status;
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setIsChangingPassword(true);
      await apiService.dashboardChangePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMustResetPassword(false);
      toast.success('Password changed');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const selectedSite = useMemo(
    () => sites.find((s) => s.id === selectedSiteId) || null,
    [sites, selectedSiteId]
  );

  const formFingerprint = useMemo(
    () =>
      JSON.stringify({
        planTier,
        bookingEnabled,
        leadCaptureEnabled,
        crmSyncEnabled,
        humanHandoffEnabled,
        humanHandoffContactInfo,
        widgetCustomizationEnabled,
        canBookInChat,
        leadFieldPhone,
        leadFieldCompany,
        leadFieldAddress,
        leadFieldJobTitle,
        leadFieldNotes,
        leadFieldMarketingConsent,
        hubspotCreateDeal,
        timezone,
        defaultLanguage,
        daySchedule,
        dateOverrides,
        slotIntervalMinutes,
        concurrentCapacity,
        maxSuggestedSlots,
        suggestionGap,
        searchDays,
        fallbackUrl,
        assignees,
        services,
        servicePricing,
        priceCurrency,
        breaks,
        unavailableWindows,
        slackWebhook,
        teamEmail,
        customerEmailFrom,
        askOutcome,
        googleReviewUrl,
        negativeFeedbackSlackWebhook,
        humanHandoffSlackWebhook,
        saveToCrm,
        automationEnabled,
        crmProvider,
        crmWebhook,
        crmAuthToken,
        hubspotAccessToken,
        advancedConfigJson,
      }),
    [
      planTier, bookingEnabled, leadCaptureEnabled, crmSyncEnabled, humanHandoffEnabled,
      humanHandoffContactInfo, widgetCustomizationEnabled, canBookInChat, leadFieldPhone,
      leadFieldCompany, leadFieldAddress, leadFieldJobTitle, leadFieldNotes, leadFieldMarketingConsent,
      hubspotCreateDeal, timezone, defaultLanguage, daySchedule, dateOverrides, slotIntervalMinutes,
      concurrentCapacity, maxSuggestedSlots, suggestionGap, searchDays, fallbackUrl, assignees,
      services, servicePricing, priceCurrency, breaks, unavailableWindows, slackWebhook, teamEmail,
      customerEmailFrom, askOutcome, googleReviewUrl, negativeFeedbackSlackWebhook, humanHandoffSlackWebhook,
      saveToCrm, automationEnabled, crmProvider, crmWebhook, crmAuthToken, hubspotAccessToken,
      advancedConfigJson,
    ]
  );

  const [baselineFingerprint, setBaselineFingerprint] = useState('');
  const skipDirtyRef = useRef(true);

  useEffect(() => {
    // After config sync / save, baseline is refreshed; ignore transient dirty
    if (skipDirtyRef.current) {
      setBaselineFingerprint(formFingerprint);
      skipDirtyRef.current = false;
    }
  }, [formFingerprint]);

  // Refresh baseline whenever site config is reloaded
  const configId = config ? JSON.stringify(config).slice(0, 80) : '';
  useEffect(() => {
    skipDirtyRef.current = true;
  }, [selectedSiteId, configId]);

  const isDirty = Boolean(baselineFingerprint) && formFingerprint !== baselineFingerprint && !skipDirtyRef.current;

  const markSavedBaseline = () => {
    skipDirtyRef.current = true;
    setBaselineFingerprint(formFingerprint);
  };

  const confirmLeaveIfDirty = () => {
    if (!isDirty) return true;
    return window.confirm('You have unsaved configuration changes. Leave this page?');
  };

  // Wrap save to reset dirty baseline after success — handleSaveConfig already syncs form;
  // force baseline refresh after save toast path by toggling skip flag in a patched call.
  const saveConfig = async () => {
    await handleSaveConfig();
    skipDirtyRef.current = true;
  };

  const refreshBilling = async () => {
    if (selectedSiteId) {
      const fresh = await apiService.dashboardGetBilling(selectedSiteId);
      setBilling(fresh);
      return;
    }
    const fresh = await apiService.dashboardGetAccountBilling();
    setBilling(fresh);
  };

  const reloadSites = async (preferSiteId?: number | null) => {
    const siteRows = await apiService.dashboardListSites();
    setSites(siteRows);
    if (preferSiteId != null && siteRows.some((s) => s.id === preferSiteId)) {
      setSelectedSiteId(preferSiteId);
    } else if (siteRows.length > 0 && selectedSiteId == null) {
      setSelectedSiteId(siteRows[0].id);
    }
    return siteRows;
  };

  return {
    // auth
    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    loginError, setLoginError,
    authReady, isAuthenticated, isPlatformAdmin, mustResetPassword, userEmail,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    // site
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    // data
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    emailConnection, isConnectingEmail,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail,
    customerEmailFrom, setCustomerEmailFrom, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    // handlers
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig: saveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    handleConnectEmail, handleDisconnectEmail,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, reloadSites, loadSiteData, isDirty, markSavedBaseline, confirmLeaveIfDirty,
  };
}

export function DashboardSiteProvider({ children }: { children: ReactNode }) {
  const value = useDashboardSiteState();
  return <DashboardSiteContext.Provider value={value}>{children}</DashboardSiteContext.Provider>;
}

export function useDashboardSite() {
  const ctx = useContext(DashboardSiteContext);
  if (!ctx) throw new Error('useDashboardSite must be used within DashboardSiteProvider');
  return ctx;
}
